import { skipToken, useMutation, useQuery } from "@tanstack/react-query";

import { type Uuid } from "_/types";

import { api, type ApiResponse } from "_/utils";

const SCOPE = "commands";
const KEYS = {
  command: (machineId: Uuid, commandId: Uuid | undefined) => [
    SCOPE,
    machineId,
    commandId,
  ],
};

/**
 * All possible commands that can be sent to a machine.
 */
export type CommandKind = "stop" | "pause";

/**
 * Discriminated union for command actions. Each kind of command may have a
 * different payload shape.
 * */
export type CommandAction = {
  kind: "stop" | "pause";
  payload: { attemptId: Uuid };
};

export type CommandStatus =
  | "pending"
  | "sent"
  | "delivered"
  | "completed"
  | "failed"
  | "expired"
  | "timedOut";

export type Command = {
  id: Uuid;
  machineId: Uuid;
  submittedBy: Uuid;
  action: CommandAction;
  status: CommandStatus;
  createdAt: string;
  expiresAt: string;
  sentAt: string | null;
  acknowledgedAt: string | null;
  endedAt: string | null;
  error: string | null;
  retryCount: number;
  metadata: Record<string, unknown>;
};

const TERMINAL_COMMAND_STATUSES: CommandStatus[] = [
  "completed",
  "failed",
  "expired",
  "timedOut",
];

const isTerminalStatus = (command: Command | undefined) =>
  command !== undefined && TERMINAL_COMMAND_STATUSES.includes(command.status);

export type SendCommandParams = {
  machineId: Uuid;
  action: CommandAction;
};

async function getCommand(
  machineId: Uuid,
  commandId: Uuid,
  signal?: AbortSignal
): Promise<Command> {
  const response = await api.get<ApiResponse<Command>>(
    `/machines/${machineId}/commands/${commandId}`,
    { signal }
  );

  return response.data.data;
}

export function usePollCommand(
  machineId: Uuid,
  commandId: Uuid | undefined,
  pollFrequencyMsecs: number
) {
  const query = useQuery({
    queryKey: KEYS.command(machineId, commandId),
    queryFn: commandId
      ? ({ signal }) => getCommand(machineId, commandId, signal)
      : skipToken,
    refetchInterval: (currentQuery) =>
      isTerminalStatus(currentQuery.state.data) ? false : pollFrequencyMsecs,
  });

  return { ...query, isTerminal: isTerminalStatus(query.data) };
}

async function sendCommand({
  machineId,
  action,
}: SendCommandParams): Promise<Command> {
  const response = await api.post<ApiResponse<Command>>(
    `/machines/${machineId}/commands`,
    action
  );

  return response.data.data;
}

export function useSendCommand() {
  return useMutation({ mutationFn: sendCommand });
}

/** Send a command and track it until it completes, fails, expires, or times out. */
export function useExecuteCommand(pollFrequencyMsecs: number) {
  const sendCommandMutation = useSendCommand();
  const submittedCommand = sendCommandMutation.data;
  const shouldPoll =
    submittedCommand !== undefined && !isTerminalStatus(submittedCommand);
  const commandQuery = usePollCommand(
    submittedCommand?.machineId ?? "",
    shouldPoll ? submittedCommand.id : undefined,
    pollFrequencyMsecs
  );
  const command = commandQuery.data ?? submittedCommand;
  const isTerminal = isTerminalStatus(command);

  return {
    send: sendCommandMutation.mutate,
    command,
    isExecuting:
      sendCommandMutation.isPending ||
      (submittedCommand !== undefined && !isTerminal && !commandQuery.isError),
    isTerminalStatus: isTerminal,
    isError: sendCommandMutation.isError || commandQuery.isError,
    error: sendCommandMutation.error ?? commandQuery.error,
    reset: sendCommandMutation.reset,
  };
}
